import React, { useState, useEffect, useCallback } from "react";
import { TbCurrentLocation } from "react-icons/tb";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LocationPicker from "../LocationPicker/LocationPicker";


const CustomerAddressUpdate = () => {
    const navigate = useNavigate();

    const storedCustomerData = JSON.parse(localStorage.getItem("customerData") || "{}");
    const addressIndexId = parseInt(localStorage.getItem("addressIndex") || "0", 10);
    const existingAddress = storedCustomerData?.addresses?.[addressIndexId] || {};
    const currentAddressId = existingAddress.addressId || 0;

    const [customerMaster, setCustomerMaster] = useState({
        customerId: storedCustomerData.customerId || "",
        addresses: [
            {
                addressId: currentAddressId,
                addLine1: existingAddress.addLine1 || "",
                addLine2: existingAddress.addLine2 || "",
                nearBy: existingAddress.nearBy || "",
                area: existingAddress.area || "",
                city: existingAddress.city || "",
                state: existingAddress.state || "",
                pincode: existingAddress.pincode || "",
                latitude: existingAddress.latitude || 0,
                longitude: existingAddress.longitude || 0,
            },
        ],
    });

    const API_BASE_URL = process.env.REACT_APP_API_KEY;
    const [latitude, setLatitude] = useState(existingAddress.latitude || 0);
    const [longitude, setLongitude] = useState(existingAddress.longitude || 0);
    const [showMap, setShowMap] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({
        area: "",
        addLine1: "",
        nearBy: "",
        city: "",
        coordinates: ""
    });

    const storedOutletData = JSON.parse(localStorage.getItem("outletdataLocal") || "[]");
    const { outletId, outletlogo, outletName } = Array.isArray(storedOutletData)
        ? storedOutletData[0] || {}
        : storedOutletData;

    const pgHeader = "Update Address";

    const addressFields = [
        { name: "area", label: "Area", required: true },
        { name: "addLine1", label: "Address Line 1", required: true },
        { name: "addLine2", label: "Address Line 2", required: false },
        { name: "nearBy", label: "Nearby Landmark", required: true },
        { name: "city", label: "City", required: true },
        { name: "state", label: "State", required: false },
        { name: "pincode", label: "Pincode", required: false },
    ];

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setCustomerMaster((prev) => {
            const updatedAddresses = [...prev.addresses];
            updatedAddresses[0][name] = value;
            return { ...prev, addresses: updatedAddresses };
        });
    };

    const validateForm = () => {
        const errors = {};
        const address = customerMaster.addresses[0];

        addressFields.forEach((field) => {
            if (field.required && !address[field.name]?.trim()) {
                errors[field.name] = `${field.label} is required`;
            }
        });

        if (!latitude || !longitude) {
            errors.coordinates = "Coordinates are required.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by this browser.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setLatitude(latitude);
                setLongitude(longitude);
                fetchAddressFromCoordinates(latitude, longitude);
            },
            (error) => {
                const errors = {
                    1: "Location permission denied. Please allow access.",
                    2: "Location information is unavailable.",
                    3: "Location request timed out. Try again.",
                    0: "An unknown error occurred.",
                };
                alert(errors[error.code] || "Unable to fetch location.");
            },
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
    }, []);

    const fetchAddressFromCoordinates = useCallback(async (lat, lon) => {
        try {
            const response = await axios.get("https://nominatim.openstreetmap.org/reverse", {
                params: { lat, lon, format: "json" },
            });

            const address = response.data.address || {};

            const updatedAddress = {
                city: address.town || address.village || address.city || "",
                state: address.state || "",
                pincode: address.postcode || "",
                latitude: lat,
                longitude: lon,
            };

            setCustomerMaster((prev) => {
                const updatedAddresses = [...prev.addresses];
                updatedAddresses[0] = {
                    ...updatedAddresses[0],
                    ...updatedAddress,
                };
                return { ...prev, addresses: updatedAddresses };
            });
        } catch (error) {
            console.error("Error fetching address:", error);
            alert("Error fetching address from coordinates.");
        }
    }, []);

    const RegisterCustomer = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);

        const updatedAddress = {
            ...customerMaster.addresses[0],
            latitude,
            longitude,
        };

        const payload = {
            customerId: customerMaster.customerId,
            addresses: [updatedAddress],
        };

        try {
            const response = await axios.post(`${API_BASE_URL}CustomerMaster/UpsertCustomerAddress`, payload);

            const addressId = response?.data?.addressId;
            if (addressId !== undefined && addressId !== null) {
                localStorage.setItem("UpdateAndAddAddressId", addressId);

                const addressIdx = addressId !== null
                ? storedCustomerData.addresses?.findIndex(address => address.addressId === addressId)
                : null;
                localStorage.setItem("addressIndex", addressIdx !== null ? addressIdx.toString() : "");

                
            }

            localStorage.removeItem("getAddress");
            navigate("/QrCartItems");
        } catch (error) {
            console.error("Error posting data", error);
            alert("Failed to save address. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <nav className="navbar navbar-light bg-light shadow-sm bg-body rounded">
                    <div className="container-fluid">
                        <a className="navbar-brand" href="#">
                            <img
                                src={outletlogo}
                                alt="Outlet Logo"
                                width={60}
                                height={40}
                                className="d-inline-block align-text-center me-2 img-fluid"
                            />
                            <strong>{outletName}</strong>
                        </a>
                    </div>
                </nav>

                <div className="col-12 mt-2 mb-2">
                    <h3 className="text-center">{pgHeader}</h3>
                </div>

                <div className="col-md-12 border p-3">
                    {/* Location Section */}
                    <div className="mb-3">
                        <label className="form-label">Location</label>
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="text"
                                style={{ width: "50%" }}
                                className={`form-control ${formErrors.coordinates ? "is-invalid" : ""}`}
                                value={latitude && longitude ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : ""}
                                placeholder="Current location coordinates"
                                readOnly
                            />
                            <button
                                className="btn btn-outline-primary"
                                onClick={getCurrentLocation}
                                type="button"
                                disabled={isSubmitting}
                            >
                                <TbCurrentLocation size={24} />
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowMap(!showMap)}
                                type="button"
                                disabled={!latitude}
                            >
                                {showMap ? "Close Map" : "Open Map"}
                            </button>
                        </div>
                        {formErrors.coordinates && (
                            <div className="invalid-feedback d-block">{formErrors.coordinates}</div>
                        )}
                    </div>

                    {showMap && (
                        <div className="mb-3">
                            <LocationPicker
                                latitude={latitude}
                                longitude={longitude}
                                setLatitude={setLatitude}
                                setLongitude={setLongitude}
                                fetchAddressFromCoordinates={fetchAddressFromCoordinates}
                            />
                        </div>
                    )}

                    {/* Address Fields */}
                    <div className="row">
                        {addressFields.map((field) => (
                            <div key={field.name} className="col-6 col-sm-6 mb-3">
                                <label className="form-label">{field.label}</label>
                                <input
                                    type="text"
                                    className={`form-control ${formErrors[field.name] ? "is-invalid" : ""}`}
                                    placeholder={field.label}
                                    name={field.name}
                                    value={customerMaster.addresses[0][field.name] || ""}
                                    onChange={handleAddressChange}
                                    disabled={isSubmitting}
                                    required={field.required}
                                />
                                {formErrors[field.name] && (
                                    <div className="invalid-feedback">{formErrors[field.name]}</div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Submit Button */}
                    <div className="d-flex justify-content-center mt-4">
                        <button
                            className="btn btn-primary px-4"
                            onClick={RegisterCustomer}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerAddressUpdate;
